/**
 * @file BlogIndex.tsx
 * @summary Article de blog — BlogIndex
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Hourglass, Search, X } from "lucide-react";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BLOG_ARTICLES, CLUSTER_LABELS, BlogCluster } from "@/data/blog-articles";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const URL = "https://crossfitchistera.fr/blog";

const CLUSTER_ORDER: BlogCluster[] = [
  "decouverte",
  "douleur",
  "reprise",
  "performance",
  "competition",
  "methode",
  "profils",
  "lieu",
  "outil",
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Blog CrossFit Chistera",
  url: URL,
  description: "Articles CrossFit, santé, reprise du sport et performance par les coachs CrossFit Chistera (Saint-Pierre-de-Chandieu, Lyon Est).",
  inLanguage: "fr-FR",
  isPartOf: { "@type": "WebSite", name: "CrossFit Chistera", url: "https://crossfitchistera.fr" },
};

export default function BlogIndex() {
  const [query, setQuery] = useState("");

  const tokens = useMemo(
    () =>
      query
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .split(/\s+/)
        .filter((t) => t.length >= 2),
    [query]
  );

  const matches = (a: typeof BLOG_ARTICLES[number]) => {
    if (tokens.length === 0) return true;
    const haystack = `${a.title} ${a.excerpt} ${CLUSTER_LABELS[a.cluster]} ${a.slug}`
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return tokens.every((t) => haystack.includes(t));
  };

  const grouped = CLUSTER_ORDER.map((c) => ({
    cluster: c,
    label: CLUSTER_LABELS[c],
    articles: BLOG_ARTICLES.filter((a) => a.cluster === c && matches(a)),
  })).filter((g) => g.articles.length > 0);

  const totalMatches = grouped.reduce((n, g) => n + g.articles.length, 0);

  return (
    <>
      <SEO
        title="Blog CrossFit Chistera — santé, reprise et performance"
        description="Articles des coachs CrossFit Chistera (Saint-Pierre-de-Chandieu, Lyon Est) : douleurs, reprise après blessure, méthode, performance, adhérents."
        canonical={URL}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-20">
          <section className="container mx-auto px-4 max-w-5xl">
            <header className="mb-8 text-center">
              <h1 className="text-4xl md:text-6xl text-foreground font-heading mb-4">Le blog CrossFit Chistera</h1>
              <p className="text-lg text-foreground/80 max-w-3xl mx-auto">
                Blog CrossFit, fitness et santé du sportif rédigé par les coachs diplômés de la box CrossFit Chistera à Saint-Pierre-de-Chandieu (Lyon Est) : guides pratiques pour débutants et confirmés, conseils pour soulager une douleur (genou, épaule, dos, hanche, coude, cervicales), protocoles de reprise du sport après blessure, méthode d'entraînement, préparation aux compétitions (Open, Hyrox, Affiliates Battle), nutrition, WOD, et témoignages d'adhérents de Saint-Pierre-de-Chandieu, Heyrieux, Chaponnay, Mions, Saint-Priest, Bonnefamille et l'Est lyonnais.
              </p>
            </header>

            <div className="mb-10 max-w-2xl mx-auto">
              <label htmlFor="blog-search" className="sr-only">Rechercher un article</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/60" aria-hidden="true" />
                <Input
                  id="blog-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher : douleur genou, débutant, prix, Lyon…"
                  className="pl-10 pr-10 h-12 bg-card/50 border-border/60 focus-visible:ring-gold"
                  aria-label="Rechercher un article par mots-clés"
                />
                {query && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuery("")}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9"
                    aria-label="Effacer la recherche"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {tokens.length > 0 && (
                <p className="mt-2 text-sm text-foreground/70 text-center" aria-live="polite">
                  {totalMatches === 0
                    ? "Aucun article ne correspond à votre recherche."
                    : `${totalMatches} article${totalMatches > 1 ? "s" : ""} trouvé${totalMatches > 1 ? "s" : ""}.`}
                </p>
              )}
            </div>

            {grouped.map((g) => (
              <section key={g.cluster} className="mb-14">
                <h2 className="text-2xl md:text-3xl font-heading text-gold mb-6 border-b border-gold/30 pb-2">{g.label}</h2>
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {g.articles.map((a) => {
                    const isPublished = a.status === "published";
                    const inner = (
                      <Card
                        className={cn(
                          "h-full transition-all border-border/50 bg-card/40",
                          isPublished
                            ? "hover:border-gold/60 hover:bg-card/70 hover:shadow-lg cursor-pointer"
                            : "opacity-60"
                        )}
                      >
                        <CardContent className="p-5 flex flex-col h-full">
                          <h3 className="text-lg font-heading text-foreground mb-2 leading-snug">{a.title}</h3>
                          <p className="text-sm text-foreground/75 leading-relaxed flex-grow">{a.excerpt}</p>
                          <div className="mt-4 flex items-center justify-between text-xs text-foreground/75">
                            <span className="inline-flex items-center gap-1">
                              {isPublished ? <Clock className="w-3 h-3" /> : <Hourglass className="w-3 h-3" />}
                              {isPublished
                                ? a.readingMinutes ? `${a.readingMinutes} min de lecture` : "Lire l'article"
                                : "Bientôt disponible"}
                            </span>
                            {isPublished && <ArrowRight className="w-4 h-4 text-gold" />}
                          </div>
                        </CardContent>
                      </Card>
                    );
                    return isPublished ? (
                      <Link key={a.slug} to={`/blog/${a.slug}`} className="block focus:outline-none focus:ring-2 focus:ring-gold rounded-lg">
                        {inner}
                      </Link>
                    ) : (
                      <div key={a.slug} aria-disabled>{inner}</div>
                    );
                  })}
                </div>
              </section>
            ))}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}
